import { useId, useState } from 'react'
import { Plus, X } from 'lucide-react'
import { TAG_STYLE_FILLED, TAG_STYLE_QUIET } from '../../../../../shared/pkg/prepacks/templateRendering.js'

const MAX_TAGS = 5
const MAX_TAG_LENGTH = 40

function unicodeLength(value) {
  return Array.from(value).length
}

const TAG_STYLES = [
  [TAG_STYLE_QUIET, 'Quiet', 'Outlined chips that never compete with the bet type or the button'],
  [TAG_STYLE_FILLED, 'Filled', 'Chips in the bet-type chip’s fill, for labels that should stand out'],
]

/**
 * Operator labels. Tags are presentation metadata only: bounded, trimmed,
 * and deduplicated case-insensitively before they reach the parent payload.
 * With tags, the operator picks how the card draws them (tagStyle).
 */
export default function PrepackTagsField({
  value = [],
  onChange,
  tagStyle = TAG_STYLE_QUIET,
  onTagStyleChange,
  disabled = false,
}) {
  const [inputVal, setInputVal] = useState('')
  const [error, setError] = useState('')
  const inputId = useId()
  const tags = Array.isArray(value) ? value.filter((tag) => typeof tag === 'string') : []

  const handleAddTag = () => {
    if (disabled) return
    const trimmed = inputVal.trim()
    if (!trimmed) return

    if (unicodeLength(trimmed) > MAX_TAG_LENGTH) {
      setError(`Tags must be ${MAX_TAG_LENGTH} characters or less.`)
      return
    }
    if (tags.length >= MAX_TAGS) {
      setError(`Maximum ${MAX_TAGS} tags allowed.`)
      return
    }
    const normalized = trimmed.toLowerCase()
    if (tags.some((tag) => tag.trim().toLowerCase() === normalized)) {
      setError('Tag already added.')
      return
    }

    setError('')
    onChange?.([...tags, trimmed])
    setInputVal('')
  }

  const handleRemoveTag = (index) => {
    if (disabled) return
    onChange?.(tags.filter((_, itemIndex) => itemIndex !== index))
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      handleAddTag()
    }
  }

  return (
    <div className="w-full">
      <div className="mb-1 flex items-center justify-between">
        <label htmlFor={inputId} className="text-xs text-[var(--prepacks-muted)]">Tags ({tags.length}/{MAX_TAGS})</label>
        {tags.length > 0 && onTagStyleChange ? (
          <div role="group" aria-label="Tag style" className="flex items-center rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] p-0.5 text-xs">
            {TAG_STYLES.map(([style, label, title]) => (
              <button key={style} type="button" aria-pressed={tagStyle === style} title={title} disabled={disabled} onClick={() => onTagStyleChange(style)} className={`rounded px-2 py-0.5 disabled:opacity-50 ${tagStyle === style ? 'bg-[var(--prepacks-raised)] font-semibold text-[var(--wf-color-text)]' : 'text-[var(--prepacks-muted)] hover:text-[var(--wf-color-text)]'}`}>{label}</button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] p-2">
        {tags.map((tag, index) => (
          <span key={`${tag}-${index}`} className="inline-flex items-center gap-1 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-raised)] px-2 py-0.5 text-xs text-[var(--wf-color-text)]">
            <span>{tag}</span>
            <button
              type="button"
              disabled={disabled}
              onClick={() => handleRemoveTag(index)}
              aria-label={`Remove tag ${tag}`}
              className="text-[var(--prepacks-muted)] hover:text-[var(--wf-color-danger)] disabled:opacity-50"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}

        {tags.length < MAX_TAGS ? (
          <div className="flex min-w-[120px] flex-1 items-center gap-1">
            <input
              id={inputId}
              type="text"
              value={inputVal}
              onChange={(event) => {
                setInputVal(event.target.value)
                setError('')
              }}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              placeholder={tags.length === 0 ? 'Add tag (e.g. Player Props)' : 'Add another tag…'}
              className="flex-1 bg-transparent px-1 text-xs text-[var(--wf-color-text)] outline-none placeholder:text-[var(--prepacks-muted)] disabled:opacity-50"
            />
            {inputVal.trim() ? (
              <button type="button" onClick={handleAddTag} disabled={disabled} aria-label="Add tag" className="text-[var(--prepacks-accent-text)] hover:text-[var(--prepacks-accent-hover)]">
                <Plus className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {error ? <p role="alert" className="mt-1 text-xs text-[var(--wf-color-danger)]">{error}</p> : null}
    </div>
  )
}

